import * as _ from 'lodash'
import { DateTime } from 'luxon'
import { STATE_PROVINCE_LIST } from 'src/data/address.form'
import { LicensePrefill } from 'src/types/scanner'

/**
 * Prefilling and cross-checking the IDP wizard from the driver's licence
 * barcode (CU-868kxk3vw).
 *
 * The analyzer returns the barcode's fields already in the wizard model's
 * shape; the only decisions left here are WHICH paths may be written and the
 * rules that a value the customer has already typed is never overwritten
 * without their explicit click (decision 2026-08-27: empty fields only, then
 * a review banner) and that a scan fills the model ONCE: the model remembers
 * which scan it was filled from (PREFILL_SOURCE_PATH), so blanking or
 * correcting a field and coming back through the scan step keeps the
 * correction (prod bug, 2026-10).
 *
 * Note on step order, because an earlier version of this comment had it
 * backwards: the scan is step 3 of the IDP wizard (requirement 7809, prod and
 * dev), and the address (step 4) and licence number/expiry/vehicles (step 5)
 * come AFTER it. So prefill fills fields the customer has not reached yet,
 * and the banner must not tell them to look at earlier steps. Name and date of
 * birth are on steps 1-2, before the scan, so in practice they are already
 * typed and prefill skips them as non-empty.
 *
 * Pure functions on the model object so they can be reasoned about without
 * Angular. The stepper builds each step's form from the model when the step
 * is shown, so writing into the model is enough for the values to appear.
 */

/** ISO/IEC 7810 ID-1 (driver's licence) aspect: 85.60 x 53.98 mm. */
export const LICENSE_ASPECT_RATIO = 85.60 / 53.98
export const LICENSE_TYPES: string[] = ['drivers_license_front', 'drivers_license_back']

/** Model paths the IDP form asks for that the barcode can answer. */
export const LICENSE_PREFILL_PATHS: string[] = [
	'first_name',
	'middle_name',
	'last_name',
	'date_of_birth',
	'drivers_license.number',
	'drivers_license.expiration_date',
	'drivers_license.address.address_1',
	'drivers_license.address.address_2',
	'drivers_license.address.city',
	'drivers_license.address.state',
	'drivers_license.address.zip',
]

const VEHICLES_PATH = 'drivers_license.vehicles'

/**
 * Where the model remembers which scan it has been filled from. A PROFILE
 * key, like everything the scan touches (the names, `drivers_license.*`
 * and the scan keys are all profile-level, and the server serves the
 * traveler's latest back across all of their applications), so a second
 * IDP application for the same traveler does not fill the blanks again.
 *
 * Without it the scan step filled every blank field each time it was shown:
 * a customer who blanked a value the barcode had got wrong (a middle name
 * that was really part of the first name) had it written back on every
 * visit and could never make the correction stick (prod, 2026-10).
 */
export const PREFILL_SOURCE_PATH = 'license_prefill.back_document_uuid'

/**
 * Scans analysed before this went live may already have filled the model
 * under the old code, which left no marker; those are only ever recorded,
 * never filled again, so a blank the customer made on purpose stays blank.
 * A scan analysed after this time with no marker has never been applied
 * (the new code marks whenever it fills), so it is safe to fill from on a
 * revisit too — the first visit's request may have been cancelled by a
 * quick Continue, failed, or never run because the phone finished while
 * the desktop was not watching. Must be AFTER the prod deploy of this
 * change: too early reopens the once-more refill for scans in the gap,
 * too late only costs the prefill in those rare cases for a few days.
 */
export const PREFILL_ONCE_SINCE = '2026-10-23T00:00:00Z'

/** Analysed after PREFILL_ONCE_SINCE, so the old code cannot have applied it. */
export function scannedSinceFix(prefill: LicensePrefill | null | undefined): boolean {
	const at = prefill?.analyzed_at ? DateTime.fromISO(prefill.analyzed_at) : null
	return !!at && at.isValid && at > DateTime.fromISO(PREFILL_ONCE_SINCE)
}

/** The scan this prefill came from, or '' when there is nothing to fill from. */
export function prefillSource(prefill: LicensePrefill | null | undefined): string {
	return (prefill?.available && prefill.source?.back_document_uuid) || ''
}

/** Has ANY scan ever been written into this model? */
export function hasPrefillMarker(model: any): boolean {
	return !!_.get(model, PREFILL_SOURCE_PATH)
}

/** Has THIS scan been recorded against the model? */
export function prefillAlreadyApplied(model: any, prefill: LicensePrefill | null | undefined): boolean {
	const source = prefillSource(prefill)
	return !!source && _.get(model, PREFILL_SOURCE_PATH) === source
}

/** Remember the scan as applied. Returns true when the model changed. */
export function markPrefillApplied(model: any, prefill: LicensePrefill | null | undefined): boolean {
	const source = prefillSource(prefill)
	if (!source || _.get(model, PREFILL_SOURCE_PATH) === source) {
		return false
	}
	_.set(model, PREFILL_SOURCE_PATH, source)
	return true
}

// Shared with passport-prefill.service.ts.
export const isEmpty = (value: any): boolean =>
	value === undefined
	|| value === null
	|| (typeof value === 'string' && value.trim() === '')
	|| (Array.isArray(value) && value.length === 0)

/**
 * Write the barcode fields into the model where the customer hasn't typed
 * anything yet. Returns the paths that were filled.
 *
 * Vehicle types come from the licence class, which is state-specific, so
 * they are only a hint: pre-ticked when the vehicles question has never
 * been shown (the multi-select writes `[]` into the model as soon as it is
 * built, so an explicit "none" is left alone), and called out in the banner
 * so they get checked.
 */
export function applyLicensePrefill(model: any, prefill: LicensePrefill): string[] {
	const applied: string[] = []
	if (!prefill?.available || !prefill.fields) {
		return applied
	}

	for (const path of LICENSE_PREFILL_PATHS) {
		const value = _.get(prefill.fields, path)
		if (isEmpty(value) || !isEmpty(_.get(model, path))) {
			continue
		}
		_.set(model, path, value)
		applied.push(path)
	}

	const vehicles = prefill.hints?.vehicles
	if (Array.isArray(vehicles) && vehicles.length > 0 && _.get(model, VEHICLES_PATH) === undefined) {
		_.set(model, VEHICLES_PATH, [...vehicles])
		applied.push(VEHICLES_PATH)
	}

	return applied
}

export interface LicenseMismatch {
	path: string
	label: string
	entered: string
	license: string
}

/** Unwrap the datepicker's legacy `{value}` shape and stringify sanely. */
export const display = (value: any): string => {
	if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
		return String(value.value ?? '')
	}
	if (value === undefined || value === null) {
		return ''
	}
	return String(value)
}

export const stripAccents = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
export const alnum = (v: any) => stripAccents(display(v)).toUpperCase().replace(/[^A-Z0-9]/g, '')
const letters = (v: any) => stripAccents(display(v)).toUpperCase().replace(/[^A-Z]/g, '')
const digits = (v: any) => display(v).replace(/\D/g, '')

export const NAME_SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V'])

/** Surname without a generational suffix ("SMITH JR" → "SMITH"). */
const surname = (v: any): string => {
	const tokens = stripAccents(display(v)).toUpperCase().split(/[^A-Z']+/).filter(Boolean)
	while (tokens.length > 1 && NAME_SUFFIXES.has(tokens[tokens.length - 1].replace(/\./g, ''))) {
		tokens.pop()
	}
	return tokens.join('').replace(/[^A-Z]/g, '')
}

/** First given name only — some states put first AND middle in the barcode's first-name field. */
const givenName = (v: any): string => {
	const tokens = stripAccents(display(v)).toUpperCase().split(/[^A-Z']+/).filter(Boolean)
	return (tokens[0] ?? '').replace(/[^A-Z]/g, '')
}

/** yyyyMMdd from anything the wizard or the barcode might hold; '' when unparseable. */
export const dateKey = (v: any): string => {
	const raw = display(v).trim()
	if (!raw) {
		return ''
	}
	for (const format of ['MM/dd/yyyy', 'M/d/yyyy', 'yyyy-MM-dd', 'MMddyyyy', 'yyyyMMdd']) {
		const parsed = DateTime.fromFormat(raw, format)
		if (parsed.isValid) {
			return parsed.toFormat('yyyyMMdd')
		}
	}
	const iso = DateTime.fromISO(raw)
	return iso.isValid ? iso.toFormat('yyyyMMdd') : ''
}

/** Two-letter code from a code or a full state/province name. */
const stateCode = (v: any): string => {
	const raw = display(v).trim()
	if (raw.length <= 3) {
		return letters(raw)
	}
	const wanted = letters(raw)
	for (const list of Object.values(STATE_PROVINCE_LIST) as { label: string, value: string }[][]) {
		const hit = list.find(option => letters(option.label) === wanted)
		if (hit) {
			return letters(hit.value)
		}
	}
	return wanted
}

/**
 * Fields worth cross-checking against the barcode, with how to compare
 * them. Free-text address lines and cities are deliberately left out: "St"
 * vs "Street" is not a mismatch and there is no cheap way to tell that
 * apart from a real one. A comparison is skipped when the barcode itself
 * says the value was truncated to fit.
 */
const COMPARISONS: { path: string, label: string, normalise: (v: any) => string, skipFlag?: string }[] = [
	{ path: 'first_name', label: 'First name', normalise: givenName, skipFlag: 'first_name_truncated' },
	{ path: 'last_name', label: 'Last name', normalise: surname, skipFlag: 'last_name_truncated' },
	{ path: 'date_of_birth', label: 'Date of birth', normalise: dateKey },
	{ path: 'drivers_license.number', label: 'License number', normalise: alnum },
	{ path: 'drivers_license.expiration_date', label: 'Expiration date', normalise: dateKey },
	{ path: 'drivers_license.address.state', label: 'State', normalise: stateCode },
	{ path: 'drivers_license.address.zip', label: 'ZIP code', normalise: v => digits(v).slice(0, 5) },
]

/**
 * Typed values that disagree with the barcode. The wizard's data steps come
 * before the scan, so this is where the barcode earns its keep: a typo in
 * the licence number or expiration date is caught before the packet ships.
 */
export function compareWithLicense(model: any, prefill: LicensePrefill): LicenseMismatch[] {
	if (!prefill?.available || !prefill.fields) {
		return []
	}
	const flags = new Set(prefill.flags ?? [])
	const mismatches: LicenseMismatch[] = []
	for (const { path, label, normalise, skipFlag } of COMPARISONS) {
		if (skipFlag && flags.has(skipFlag)) {
			continue
		}
		const entered = _.get(model, path)
		const license = _.get(prefill.fields, path)
		if (isEmpty(entered) || isEmpty(license)) {
			continue
		}
		const a = normalise(entered)
		const b = normalise(license)
		// Unparseable on either side is "can't tell", not "wrong".
		if (!a || !b || a === b) {
			continue
		}
		mismatches.push({ path, label, entered: display(entered), license: display(license) })
	}
	return mismatches
}

/** Overwrite the mismatched paths with the licence's values (customer's explicit choice). */
export function applyLicenseValues(model: any, prefill: LicensePrefill, mismatches: LicenseMismatch[]): string[] {
	const applied: string[] = []
	for (const { path } of mismatches) {
		const value = _.get(prefill?.fields, path)
		if (isEmpty(value)) {
			continue
		}
		_.set(model, path, value)
		applied.push(path)
	}
	return applied
}

/**
 * "your name, date of birth, address, license number and expiration date"
 * — for the review banner.
 */
export function describePrefill(paths: string[]): string {
	const groups: { label: string, match: (p: string) => boolean }[] = [
		{ label: 'name', match: p => ['first_name', 'middle_name', 'last_name'].includes(p) },
		{ label: 'date of birth', match: p => p === 'date_of_birth' },
		{ label: 'address', match: p => p.startsWith('drivers_license.address.') },
		{ label: 'license number', match: p => p === 'drivers_license.number' },
		{ label: 'expiration date', match: p => p === 'drivers_license.expiration_date' },
		{ label: 'vehicle types', match: p => p === VEHICLES_PATH },
	]
	return joinLabels(groups.filter(g => paths.some(g.match)).map(g => g.label))
}

/** "a", "a and b", "a, b and c". */
export function joinLabels(labels: string[]): string {
	if (labels.length === 0) {
		return ''
	}
	if (labels.length === 1) {
		return labels[0]
	}
	return labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1]
}
